import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { PageInfoType } from '@op/types/common';
import { DBO_TM } from '@op/utils/format';
import { gotoDownload } from '@opm/pages/Download';
import { getBilibiliVideoList } from '@opm/services';
import { useEffect, useMemo, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';
import { BILIBILI_DEFAULT_SORT } from '../tabs/BilibiliTab/constants';

interface IUseBilibiliVideoDataParams {
  /** 由 useFilterCommon 提供的合并后 filter 值（含 start_time/end_time/date_type/channel/video_tag） */
  filter: any;
  defaultPageInfo?: { page: number; page_size: number; };
}

/**
 * 将分页缓存数组转换为扁平列表 + 当前已加载的页数
 */
function getListData(data: any[][]) {
  const result: any[] = [];
  let page = 1;
  data.every(item => {
    if (item) {
      result.push(...item);
      page += 1;
      return true;
    }
    return false;
  });
  return { page, result };
}

/**
 * Bilibili 视频列表 Hook
 *
 * 提供 X tab 同款全功能（sortBy / keyword / operation / pageInfo / download / changePageInfo），
 * 并在请求层做 B 站参数语义转换：
 * - UI channel='all' → API channel: []（不过滤）
 * - UI channel='game'  → API channel: ['game']
 * - UI video_tag='' → 不传 video_tag
 * - UI search_text='' → 不传 search_text
 */
export function useBilibiliVideoData({
  filter,
  defaultPageInfo = { page: 1, page_size: 40 },
}: IUseBilibiliVideoDataParams) {
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    by: BILIBILI_DEFAULT_SORT.by,
    order: BILIBILI_DEFAULT_SORT.order,
  });
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({ defaultPageInfo });
  const [dataRef, data, setData] = useStateRef<any[][]>([]);
  const [total, setTotal] = useState(0);
  /** 各指标全局最大值，由接口随 list 一并返回；用于表格进度条的分母 */
  const [max, setMax] = useState<Record<string, number>>({});
  const { wrapperCancelRequest, loading } = useCancelRequestHook();
  const [operation, setOperation] = useState<'card' | 'table'>('card');

  function resetData() {
    setData([]);
    setMax({});
    resetPageInfo();
  }

  function changeSortBy(sortByValue: SortBy<string>) {
    setSortBy(sortByValue);
    resetData();
  }

  function changeKeyword(keywordValue: string) {
    setKeyword(keywordValue);
    resetData();
  }

  /**
   * 构造 API 请求参数。
   *
   * FilterCommon 内 BilibiliCategory / BilibiliTag 已是多选 + 全选，backValue 三态约定：
   *   - `[]`           → 全选 / 不过滤（API 拿到空数组即视为不限制）
   *   - `['-99999']`   → 全不选（用户主动清空）/ 哨兵值，后端精确匹配返回空结果
   *   - `[v1, v2,...]` → 部分选中 / IN 匹配
   *
   * 这里全部直接透传到 payload，由后端按约定处理。
   * 注：当前 API 文档 video_tag 类型为 string；前端按数组发送，待后端升级 video_tag 为 []string。
   */
  const params = useMemo(() => {
    if (!filter) return {};
    const payload: any = {
      ...filter,
      ...pageInfo,
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
    };

    if (keywordRef.current) {
      payload.search_text = keywordRef.current;
    }
    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pageInfo, sortBy, keyword]);

  function request() {
    if (!filter?.start_time) return;
    wrapperCancelRequest(getBilibiliVideoList, params)
      .then((res: any) => {
        if (!res) return;
        const { list, total: nextTotal, max: nextMax } = res;
        if (dataRef.current) {
          dataRef.current[pageInfo.page - 1] = list || [];
          setData([...dataRef.current]);
        }
        setTotal(nextTotal || 0);
        // max 由接口提供（全局最大值，跨页保持一致）；缺省时保留为空对象
        setMax(nextMax && typeof nextMax === 'object' ? nextMax : {});
      })
      .catch(() => {
        const { page } = getListData(dataRef.current || []);
        if (page > 0) {
          changePageInfoNoRender({ ...pageInfo, page: page - 1 });
        }
      });
  }

  /**
   * 请求触发：用 setTimeout(0) 兜底批处理。
   *
   * 原因：FilterCommon 内部用 `setTimeout(() => handleConfirm(value), 0)` 调用我们传入的
   * `confirm` 回调（详见 src/pages/marketing/components/FilterCommon/index.tsx 第 173 行）。
   * 由于回调在 setTimeout 内执行，React 17 不会自动批处理其中的多次 setState：
   *   - setFilter(value)            → 触发 re-render，params(filter 变) → 旧 effect fire
   *   - setData([]) + setPageInfo() → 触发 re-render，params(pageInfo 变) → 新 effect fire
   * 这会导致同一次 Confirm 触发两次 request。
   *
   * 解决：useEffect 内用 setTimeout(0) 作为微队列出队点，等同一 task 内所有 setState 完成
   * 后再发请求。多次 useEffect 重订阅会 clearTimeout 上一轮 timer，最终只剩一次 request。
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      const { page } = getListData(dataRef.current || []);
      if (pageInfo.page >= page) {
        request();
      }
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  /**
   * 跳转下载页：透传 params（已含 channel/video_tag 的三态约定值、时间区间、排序、关键词）。
   *
   * 流程：
   *  1. gotoDownload 把 { key, fileName, filter } 存 localStorage，open `/marketing/Download?id=xxx`
   *  2. 下载页按 key 在 Download/config/index.ts 注册表里查找 MarketingVideoBilibiliDownload
   *  3. 用我们这边的 columns + downloadFunc(filter) 拉满全部数据导出 CSV
   *
   * 注意：必须保证 MarketingVideoBilibiliDownload 已在 Download/config/Bilibili.ts 注册，
   *      否则下载页会因为 key 找不到而失败。
   */
  function download() {
    if (!filter?.start_time) return;
    gotoDownload({
      key: 'MarketingVideoBilibiliDownload',
      fileName: `Videos_Bilibili_${DBO_TM.timeTitle(filter)}`,
      filter: params,
    });
  }

  function changePageInfoData(localPageInfo: PageInfoType) {
    if (pageInfo.page_size !== localPageInfo.page_size) {
      setData([]);
    }
    changePageInfo(localPageInfo);
  }

  function nextPageChange() {
    const { page, page_size } = pageInfo;
    if (loading || total <= page * page_size) return;
    if (operation === 'table') return;
    nextPage();
  }

  function changeOperation(value: 'card' | 'table') {
    setOperation(value);
    if (value === 'table') {
      changePageInfoNoRender({ ...pageInfo, page: 1 });
    } else {
      const { page } = getListData(dataRef.current || []);
      changePageInfoNoRender({ ...pageInfo, page: page - 1 });
    }
  }

  const cardRecords = useMemo(() => getListData(data || []).result, [data]);
  const tableRecords = useMemo(
    () => data?.[pageInfo.page - 1] || [],
    [data, pageInfo],
  );

  return {
    data,
    records: cardRecords,
    cardRecords,
    tableRecords,
    total,
    max,
    pageInfo,
    changePageInfo: changePageInfoData,
    nextPage: nextPageChange,
    loading,
    sortBy,
    keyword,
    setKeyword: changeKeyword,
    setSortBy: changeSortBy,
    download,
    operation,
    setOperation: changeOperation,
    resetData,
  };
}
